import { useState, useRef, useEffect } from 'react';
import { faqs } from '../../../mocks/home';

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 bg-white" id="faq" itemScope itemType="https://schema.org/FAQPage">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="h-0.5 w-8 bg-teal"></div>
            <span className="text-teal font-body text-xs tracking-widest uppercase font-medium">FAQ</span>
            <div className="h-0.5 w-8 bg-teal"></div>
          </div>
          <h2 className="font-heading font-800 text-navy text-4xl lg:text-5xl mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-charcoal/60 font-body text-lg max-w-xl mx-auto">
            Everything you need to know about moving with TrueCare in Minneapolis and the Twin Cities.
          </p>
        </div>

        {/* FAQ Items */}
        <div
          className={`space-y-3 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {faqs.map((faq, i) => (
            <div
              key={i}
              className={`border rounded-xl overflow-hidden transition-all duration-300 ${
                openIndex === i ? 'border-teal/30 bg-teal/5' : 'border-navy/10 bg-cream hover:border-teal/20'
              }`}
              itemScope
              itemType="https://schema.org/Question"
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer"
              >
                <span className="font-heading font-600 text-navy text-sm pr-4" itemProp="name">{faq.q}</span>
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 transition-all duration-300 ${
                  openIndex === i ? 'bg-teal text-navy' : 'bg-navy/5 text-navy/50'
                }`}>
                  <i className={`ri-${openIndex === i ? 'subtract' : 'add'}-line text-sm`}></i>
                </div>
              </button>
              {openIndex === i && (
                <div
                  className="px-6 pb-5"
                  itemProp="acceptedAnswer"
                  itemScope
                  itemType="https://schema.org/Answer"
                >
                  <p className="text-charcoal/65 font-body text-sm leading-relaxed" itemProp="text">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
